/**
 * Patient Biosimilar Program — field-level effects (deny alternatives → Bridge / Copay / 340B).
 */
import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FieldHandler } from "@neu/libui";

import type { Form } from "@formily/core";

const DENY_ALT_FIELDS = [
	"is_bridge_available",
	"start_bridge_app",
	"bridge_program",
	"is_copay_available",
	"start_copay_app",
	"copay_program",
	"is_340b_available",
	"start_340b_app",
	"is_provider_available",
] as const;

function clearBiosimilarDenyAlternatives(form: Form) {
	form.setFieldState("show_post_bridge_alts", (s) => {
		s.value = "No";
	});
	for (const name of DENY_ALT_FIELDS) {
		form.setFieldState(name, (s) => {
			s.value = undefined;
		});
	}
}

// Tracks post-bridge alt flag while Denied (mirrors PAPBridgeAltGate).
export const BiosimilarBridgeAltGate: FieldHandler = (field, { form }) => {
	const status = form.getFieldState("status")?.value ?? field.value;
	form.setFieldState("show_post_bridge_alts", (s) => {
		s.value = status === "Denied" ? "Yes" : "No";
	});
	return "";
};

let biosimilarStatusChangeSeq = 0;

// When Denied: auto-lookup Bridge / Copay / 340B from Short Brand.
// When leaving Denied: clear deny-alt fields.
export const BiosimilarStatusChange: FieldHandler = (field, props) => {
	const { form, ...rest } = props;
	const status = form.getFieldState("status")?.value ?? field.value;

	if (status !== "Denied") {
		biosimilarStatusChangeSeq += 1;
		clearBiosimilarDenyAlternatives(form);
		return "";
	}

	const seq = ++biosimilarStatusChangeSeq;

	void (async () => {
		const medId =
			rest.parentForm?.getFieldState("medication_id")?.value?.id;

		if (!medId) return;

		try {
			const invData = await fetchAPI(buildApiUrl(`/inventory/${medId}`));
			if (seq !== biosimilarStatusChangeSeq) return;
			if (form.getFieldState("status")?.value !== "Denied") return;

			const inv = Array.isArray(invData) ? invData[0] : invData;
			if (!inv.brand_name_id?.id) return;

			const brandData = await fetchAPI(
				buildApiUrl(`/drug_brand_short/${inv.brand_name_id?.id}`)
			);
			if (seq !== biosimilarStatusChangeSeq) return;
			if (form.getFieldState("status")?.value !== "Denied") return;

			const brand = Array.isArray(brandData) ? brandData[0] : brandData;
			if (!brand) return;

			form.setFieldState("is_bridge_available", (s) => {
				s.value = brand.bridge_program_available || "No";
			});
			form.setFieldState("is_copay_available", (s) => {
				s.value = brand.copay_available || "No";
			});
			form.setFieldState("is_340b_available", (s) => {
				s.value = brand.discount_drug_program_available || "No";
			});

			if (Array.isArray(brand.drug_bridge) && brand.drug_bridge.length) {
				const bridgeOptions = brand.drug_bridge.map((record: any) => ({
					value: record.id,
					label: record.name,
				}));
				form.setFieldState("bridge_program", (s) => {
					s.dataSource = bridgeOptions;
				});
			}

			if (Array.isArray(brand.drug_copay) && brand.drug_copay.length) {
				const copayOptions = brand.drug_copay.map((record: any) => ({
					value: record.id,
					label: record.name,
				}));
				form.setFieldState("copay_program", (s) => {
					s.dataSource = copayOptions;
				});
			}

			BiosimilarBridgeAltGate(field, props);
		} catch (err) {
			console.error("BiosimilarStatusChange: API error", err);
		}
	})();
	return "";
};

export const BiosimilarCheckCondition: FieldHandler = (field, { form }) => {
	const startCopayApp = form.getFieldState("start_copay_app")?.value;
	const is340bAvailable = form.getFieldState("is_340b_available")?.value;

	form.setFieldState("condition_chck", (s) => {
		s.value = "No";
	});

	if (is340bAvailable === "Yes" && startCopayApp !== "Yes") {
		form.setFieldState("condition_chck", (s) => {
			s.value = "Yes";
		});
	}
	return "";
};

export const BiosimilarCheckForAlert: FieldHandler = (field, { form }) => {
	form.setFieldState("show_alert", (s) => {
		s.value = "No";
	});

	const startBridgeApp = form.getFieldState("start_bridge_app")?.value;
	const startCopayApp = form.getFieldState("start_copay_app")?.value;
	const start340bApp = form.getFieldState("start_340b_app")?.value;
	const isBridgeAvailable = form.getFieldState("is_bridge_available")?.value;
	const isCopayAvailable = form.getFieldState("is_copay_available")?.value;
	const is340bAvailable = form.getFieldState("is_340b_available")?.value;

	if (startBridgeApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "Bridge Program added to the case";
		});
	}

	if (startCopayApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "Copay added to the case";
		});
	}

	if (start340bApp === "Yes") {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value = "340B/discount drug program will added to the case";
		});
	}

	if (
		isBridgeAvailable === "No" &&
		isCopayAvailable === "No" &&
		is340bAvailable === "No"
	) {
		form.setFieldState("show_alert", (s) => {
			s.value = "Yes";
		});
		form.setFieldState("alert", (s) => {
			s.value =
				"Drug has no Bridge, copay, or 340B options available, you will need to communicate with the member regarding other options";
		});
	}

	return "";
};
